import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, Gift, Layers, Users, Settings, Download, LogOut, 
  ChevronRight, Menu, X, Shield, Activity
} from "lucide-react";
import { useState } from "react";

const AdminLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Analytics", exact: true },
    { path: "/admin/activity", icon: Activity, label: "User Activity" },
    { path: "/admin/rewards", icon: Gift, label: "Rewards Catalog" },
    { path: "/admin/tiers", icon: Layers, label: "Tier Settings" },
    { path: "/admin/users", icon: Users, label: "Users & Roles" },
    { path: "/admin/settings", icon: Settings, label: "Points Config" },
    { path: "/admin/exports", icon: Download, label: "Data Exports" },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:sticky top-0 left-0 z-50 h-screen w-64 bg-[#0F0F0F] border-r border-[#252525]
        transform transition-transform duration-300 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#252525]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#C9A962] to-[#A68B4B] flex items-center justify-center">
                  <Shield size={18} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <h1 className="text-lg font-display font-medium text-gradient-luxury">OCULUS</h1>
                  <p className="text-[#C9A962] text-xs tracking-widest">ADMIN</p>
                </div>
              </div>
              <button 
                className="md:hidden text-[#5A5A5A] hover:text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                  ${isActive(item.path, item.exact) 
                    ? 'bg-[#C9A962]/10 text-[#C9A962] shadow-[0_0_20px_rgba(201,169,98,0.1)]' 
                    : 'text-[#5A5A5A] hover:text-white hover:bg-[#1A1A1A]'
                  }
                `}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon size={20} />
                <span className="font-medium text-sm">{item.label}</span>
                {isActive(item.path, item.exact) && (
                  <ChevronRight size={16} className="ml-auto" />
                )}
              </NavLink>
            ))}
          </nav>

          {/* Staff Portal Link */}
          <div className="px-4 pb-2">
            <NavLink
              to="/staff"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#5A5A5A] hover:text-white hover:bg-[#1A1A1A] transition-all duration-200"
              data-testid="admin-staff-portal-link"
            >
              <Users size={20} />
              <span className="font-medium text-sm">Staff Portal</span>
            </NavLink>
          </div>

          {/* User & Logout */}
          <div className="p-4 border-t border-[#252525]">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B] flex items-center justify-center">
                <span className="text-[#0A0A0A] font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-[#C9A962]">Administrator</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-[#5A5A5A] hover:text-[#D9534F] transition-colors"
                data-testid="admin-logout-btn"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 glass border-b border-[#252525]">
          <div className="px-4 md:px-8 py-5 flex items-center gap-4">
            <button 
              className="md:hidden text-[#5A5A5A] hover:text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={24} />
            </button>
            <div>
              <h2 className="text-xl font-display font-medium text-white">{title}</h2>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
